import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Make the Ask Allie panel draggable by its header, at either size.
 *
 * Users working alongside an answer want Allie out of the way of the thing the
 * answer is about, so grabbing the header lets them put it anywhere on screen.
 *
 * The gesture mechanics (pointer/touch, user-select suppression, ignoring clicks
 * on controls) come from react-draggable's DraggableCore, which is already a
 * dependency. DraggableCore is the "events only, no transform" variant, which is
 * what a Drawer needs: the movable element is antd's own content wrapper, reached
 * through `styles.wrapper`, not a child we could wrap and translate ourselves.
 * This hook is the translator between the two — it owns the offset and the clamp.
 *
 * The two sizes need different treatment:
 *   - The compact card is already a floating card with its own corner geometry, so
 *     a drag is a pure translate. Nothing about its shape changes.
 *   - The full panel is docked to the right edge at full viewport height, which
 *     cannot move vertically without looking broken. Grabbing it converts it to a
 *     fixed-height window in the same gesture (`undockOnDrag`). The top edge stays
 *     put, so it reads as a shrink rather than a jump.
 *
 * Position resets when the panel closes and when it changes size, since both
 * anchor the panel somewhere new and a stale offset would be measured against
 * geometry that no longer exists.
 *
 * @param {object}  opts
 * @param {boolean} opts.enabled       true while the panel is open
 * @param {boolean} opts.undockOnDrag  true at full size: convert to a window on grab
 * @param {any}     opts.resetKey      changing this value returns the panel home
 */

// Wrapper element that `styles.wrapper` on the antd Drawer lands on — the panel
// itself. Applied via classNames={{ wrapper: "sb-assist-offcanvas" }} in AskAllie,
// which rc-drawer merges onto `.ant-drawer-content-wrapper`.
export const PANEL_SELECTOR = ".sb-assist-offcanvas";

// Header controls that must keep behaving like controls, not drag handles. Passed
// to DraggableCore as `cancel`, so it never starts a drag from one of these.
export const DRAG_CANCEL =
  "button, a, input, textarea, [role='tab'], .ant-tabs, .ant-tag";

const MAX_FLOATING_HEIGHT = 720; // px — tall enough to read, short enough to move
const VIEWPORT_GAP = 32;         // px of breathing room above + below
const EDGE_KEEP = 96;            // px of panel that must stay on screen sideways
const BOTTOM_KEEP = 56;          // keep at least the header row grabbable

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getPanel = () => document.querySelector(PANEL_SELECTOR);

/**
 * How far the panel may be moved from where it currently sits, in offset terms.
 * Derived from the live rect so it works for either size without special-casing:
 * the docked position is the rect minus the offset already applied to it.
 */
const clampRange = (rect, offset) => {
  const baseLeft = rect.left - offset.x;
  const baseTop = rect.top - offset.y;
  return {
    minX: EDGE_KEEP - baseLeft - rect.width,
    maxX: window.innerWidth - EDGE_KEEP - baseLeft,
    minY: -baseTop,
    maxY: window.innerHeight - BOTTOM_KEEP - baseTop,
  };
};

const useAllieDrag = ({ enabled, undockOnDrag = false, resetKey }) => {
  // Offset from the panel's docked position. {0,0} is "wherever CSS put it".
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  // Sticks once a full-size panel has been lifted out of the right-edge dock.
  const [isUndocked, setIsUndocked] = useState(false);

  // DraggableCore reports deltas, not absolutes, so the running offset and the
  // grab-time clamp range live in refs — state would lag behind fast pointers.
  const offsetRef = useRef(offset);
  const rangeRef = useRef(null);
  // The panel element, for the direct writes below.
  const panelRef = useRef(null);

  // A setState per pointermove re-renders the whole panel — message list, bubbles,
  // every markdown answer — which drops frames and makes the drag judder behind the
  // cursor. During the gesture the transform is written straight to the element
  // instead, and React is told once, on drop. `live` keeps the two in agreement:
  // any re-render that happens mid-drag (a streaming reply, say) renders from the
  // ref, so it paints where the panel actually is rather than snapping back to the
  // last committed offset.
  const live = isDragging ? offsetRef.current : offset;
  const hasMoved = live.x !== 0 || live.y !== 0;

  const applyOffset = useCallback((next, { commit = false } = {}) => {
    offsetRef.current = next;
    // Written synchronously, NOT deferred to requestAnimationFrame: the browser
    // already coalesces pointer moves to about one per frame, and this only writes
    // a composited property (no layout is read anywhere during a drag), so a frame
    // of queueing latency would buy nothing and show up as the panel trailing the
    // cursor.
    const panel = panelRef.current;
    if (panel) {
      // translate3d keeps the panel on its own compositor layer, so moving it never
      // repaints the page underneath.
      panel.style.transform = `translate3d(${next.x}px, ${next.y}px, 0)`;
    }
    if (commit) setOffset(next);
  }, []);

  const dock = useCallback(() => {
    rangeRef.current = null;
    // Clear what was written directly: React never managed transform/willChange, so
    // dropping them from the style object would not remove them.
    if (panelRef.current) {
      panelRef.current.style.transform = "";
      panelRef.current.style.willChange = "";
      panelRef.current = null;
    }
    offsetRef.current = { x: 0, y: 0 };
    setIsUndocked(false);
    setIsDragging(false);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Closing, or switching size, anchors the panel somewhere new — a remembered
  // offset would be measured against geometry that no longer applies.
  useEffect(() => {
    dock();
  }, [enabled, resetKey, dock]);

  // A viewport that shrank can leave a moved panel unreachable; pull it back into
  // range rather than stranding it (or yanking it home on every resize).
  useEffect(() => {
    if (!hasMoved) return;
    const onResize = () => {
      const panel = getPanel();
      if (!panel) return;
      const current = offsetRef.current;
      const range = clampRange(panel.getBoundingClientRect(), current);
      panelRef.current = panel;
      applyOffset(
        {
          x: clamp(current.x, range.minX, range.maxX),
          y: clamp(current.y, range.minY, range.maxY),
        },
        { commit: true }
      );
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [hasMoved, applyOffset]);

  const onStart = useCallback(() => {
    const panel = getPanel();
    if (!panel) return false; // aborts the gesture
    panelRef.current = panel;
    rangeRef.current = clampRange(panel.getBoundingClientRect(), offsetRef.current);

    // Set these on the element rather than waiting for the re-render: the first
    // pointermove can arrive before React commits, and moving with antd's 0.3s
    // transform transition still live is what makes the panel swim after the
    // cursor instead of tracking it.
    panel.style.transition = "none";
    panel.style.willChange = "transform";

    // Undock before any movement, so the height change and the move are one
    // gesture instead of a jump partway through.
    if (undockOnDrag) setIsUndocked(true);
    setIsDragging(true);
    return undefined;
  }, [undockOnDrag]);

  const onDrag = useCallback(
    (_e, data) => {
      const range = rangeRef.current;
      if (!range) return;
      const current = offsetRef.current;
      applyOffset({
        x: clamp(current.x + data.deltaX, range.minX, range.maxX),
        y: clamp(current.y + data.deltaY, range.minY, range.maxY),
      });
    },
    [applyOffset]
  );

  const onStop = useCallback(() => {
    rangeRef.current = null;
    if (panelRef.current) panelRef.current.style.willChange = "";
    // Hand the final position to React, so the offset survives later re-renders.
    applyOffset(offsetRef.current, { commit: true });
    setIsDragging(false);
  }, [applyOffset]);

  /** Spread onto <DraggableCore> around the header. */
  const draggableProps = {
    onStart,
    onDrag,
    onStop,
    cancel: DRAG_CANCEL,
    disabled: !enabled,
    // Measure the pointer against the BODY, not the header's offsetParent.
    //
    // react-draggable derives each delta via offsetXYFromParent, which subtracts
    // offsetParent.getBoundingClientRect().left. The header's offsetParent is the
    // drawer wrapper — the element this hook translates — so by default every
    // delta is measured in a reference frame that moves with the panel. The frame
    // trails the pointer by a paint, so the subtracted rect alternates between the
    // old and new position and the deltas oscillate: the panel shakes, and travels
    // at roughly half the pointer's distance. Pinning the frame to the body makes
    // offsetXYFromParent take its isBody branch (pure viewport coordinates), so a
    // delta means the same thing no matter where the panel currently sits.
    offsetParent: typeof document === "undefined" ? undefined : document.body,
  };

  /** Spread onto the header itself — cursor plus double-click to reset. */
  const dragHandleProps = enabled
    ? {
        onDoubleClick: (e) => {
          if (e.target.closest?.(DRAG_CANCEL)) return;
          dock();
        },
        style: { cursor: isDragging ? "grabbing" : "grab" },
        title: hasMoved
          ? "Drag to move — double-click to reset"
          : "Drag to move Allie anywhere",
      }
    : {};

  /** Merge into the Drawer's styles.wrapper. Null while untouched (no override). */
  const wrapperStyle =
    hasMoved || isUndocked
      ? {
          transform: `translate3d(${live.x}px, ${live.y}px, 0)`,
          // Full size only: trade full-height-docked for a movable window. The
          // compact card already has its own floating geometry, so leave it be.
          ...(isUndocked
            ? {
                top: 0,
                bottom: "auto",
                height: `min(${MAX_FLOATING_HEIGHT}px, calc(100vh - ${VIEWPORT_GAP}px))`,
                borderRadius: 12,
                overflow: "hidden",
                boxShadow: "0 8px 28px rgba(0, 0, 0, 0.22)",
              }
            : null),
          // antd animates the wrapper's transform on open/close; a transition here
          // would make the panel lag a pixel behind the pointer.
          transition: isDragging ? "none" : undefined,
        }
      : null;

  return {
    draggableProps,
    dragHandleProps,
    wrapperStyle,
    isUndocked,
    isDragging,
    hasMoved,
    dock,
  };
};

export default useAllieDrag;
